import { Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { IconBook, IconBulb, IconHeart, IconHome, IconLogout, IconMessage, IconMicrophone, IconSchool, IconUsers, IconUsersPlus     } from "@tabler/icons-react";
import { signOut } from "firebase/auth";
import React, { useContext, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import loadingGif from "../../assets/ai-loading1.webm";
import { auth } from "../../config/firebase";
import { Context as AppContext } from "../../context/AppContext";
import { enums } from "../../utils/enums";
import { Header, ShareThought, ToggleMenu } from "./Header";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";

export const MainLayout = ({children, onClose}) => {
    const [openDrawer, setOpenDrawer] = useState(false);
    const {state: {currentUser}} = useContext(AppContext);
    
    if (!currentUser) {
        return <LoadingScreen/>
    }
    
    return (
        <Box
            sx={{
                bgcolor: '#FFFFFF',
                width: "100%",
                height: '100vh',
                display: "flex",
                flexDirection: "column",
                overflow: 'hidden'
            }}
        >
            <Stack
                sx={{
                    bgcolor: "#FFFFFF",
                    boxShadow: {xs: 0, md: 8},
                    flex: 1,
                    height: '100%',
                    overflow: 'hidden'
                }}
            >
                <Header onToggle={() => setOpenDrawer(!openDrawer)}/>
                <Stack direction={"row"} sx={{flex: 1, height: '100%', overflow: 'hidden'}}>
                    <SideBar isOpen={openDrawer} onToggle={() => setOpenDrawer(!openDrawer)} onClose={onClose}/>
                    <Box
                        sx={{
                            flex: 1,
                            bgcolor: '#FFFFFF',
                            overflow: 'auto',
                            height: '100%'
                        }}
                    >
                        {children}
                    </Box>
                </Stack>
            </Stack>
        </Box>
    )
}

export const SideBar = ({isOpen, onToggle, onClose}) => {

    return (
        <>
            {
                isOpen &&
                <Drawer
                    anchor={"left"}
                    sx={{
                        display: {xs: "block", md: "none"},
                        height: '90vh',
                        maxHeight: '90vh',

                    }}
                    PaperProps={{sx: {width: "100%", maxWidth: "320px"}}}
                    open={isOpen}
                    onClose={onToggle}
                >
                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={1} sx={{p: 1}}>
                        <ToggleMenu onToggle={onToggle}/>
                    </Stack>
                    <SideBarItems onClose={onClose}/>
                </Drawer>
            }
            <Box
                sx={{
                    display: {xs: "none", md: "block"},
                    width: "100%",
                    height: '100%',
                    maxWidth: "280px",
                    borderRight: '1px solid #e2e8f0',
                    position: "sticky",
                    alignSelf: "flex-start",
                    top: 0,
                    bgcolor: '#fafbfc',
                    fontFamily: 'Poppins, system-ui, -apple-system, sans-serif'
                }}
                component={"aside"}
            >
                <SideBarItems/>
            </Box>
        </>

    )
}

const SideBarItems = ({onClose}) => {
    const {state: {currentUser}} = useContext(AppContext);
    const theme = useTheme();
    const iconColor = theme.palette.primary.main;
    const location = useLocation();

    const isActivePath = (path) => {
        return location.pathname.startsWith(path);
    };

    return (
        <>
            <Box sx={{display: {xs: "block", md: "none"}, mx: 2}}>
                <ShareThought fullWidth/>
            </Box>

            <List sx={{ px: 2, py: 1 }}>
                {[enums.ADMIN, enums.STUDENT].includes(currentUser?.role)
                    &&
                    <>
                        <ListItem sx={{ px: 1, py: 0.5 }}>
                            <ListItemText
                                primary={"For Students"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: '0.05em'
                                    }
                                }}
                            />
                        </ListItem>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/student"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/student') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/student') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/student') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/student') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconHome size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Student Homepage"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/student') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/student') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/student-journal"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/student-journal') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/student-journal') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/student-journal') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/student-journal') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconBook size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Student AI Journal"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/student-journal') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/student-journal') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </>
                }
            </List>

            <List sx={{ px: 2, py: 0 }}>
                {[enums.ADMIN, enums.TEACHER].includes(currentUser?.role)
                    &&
                    <>
                        <ListItem sx={{ px: 1, py: 0.5, mt: 2 }}>
                            <ListItemText
                                primary={"For Teachers"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: '0.05em'
                                    }
                                }}
                            />
                        </ListItem>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/resource-hub"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/resource-hub') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/resource-hub') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/resource-hub') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/resource-hub') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconHome size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Resource hub"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/resource-hub') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/resource-hub') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/class-gpt"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/class-gpt') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/class-gpt') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/class-gpt') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/class-gpt') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconSchool size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"In-Class GPT"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/class-gpt') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/class-gpt') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/master-prompts"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/master-prompts') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/master-prompts') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/master-prompts') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/master-prompts') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconBook size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Master Prompts"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/master-prompts') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/master-prompts') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>

                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/student-roster"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/student-roster') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/student-roster') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/student-roster') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/student-roster') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconUsers size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Your Students"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/student-roster') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/student-roster') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>

                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/ai-designer"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/ai-designer') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/ai-designer') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/ai-designer') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: '36px' }}>
                                <video
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '4px'
                                    }}
                                    autoPlay
                                    loop
                                    muted
                                    src={loadingGif}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={"AI Curriculum Designer"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/ai-designer') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/ai-designer') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </>
                }

                {[enums.MENTOR, enums.ADMIN].includes(currentUser?.role)
                    &&
                    <>
                        <ListItem sx={{ px: 1, py: 0.5, mt: 2 }}>
                            <ListItemText
                                primary={"Mentorships"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: '0.05em'
                                    }
                                }}
                            />
                        </ListItem>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/mentor"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/mentor') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/mentor') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/mentor') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/mentor') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconSchool size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Mentor Dashboard"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/mentor') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/mentor') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/applicants"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/applicants') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/applicants') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/applicants') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/applicants') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconUsers size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Applicants"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/applicants') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/applicants') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/ai-internship-designer"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/ai-internship-designer') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/ai-internship-designer') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/ai-internship-designer') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: '36px' }}>
                                <video
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                        borderRadius: '4px'
                                    }}
                                    autoPlay
                                    loop
                                    muted
                                    src={loadingGif}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={"AI Mentorship Designer"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/ai-internship-designer') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/ai-internship-designer') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </>}
                {[enums.INTERN, enums.ADMIN].includes(currentUser?.role) &&
                    <>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/intern"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/intern') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/intern') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/intern') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/intern') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconSchool size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Intern Dashboard"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/intern') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/intern') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/mentors"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/mentors') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/mentors') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/mentors') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/mentors') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconMicrophone size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Mentors"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/mentors') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/mentors') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </>
                }
                {[enums.ADMIN].includes(currentUser?.role)
                    &&
                    <>
                        <ListItem sx={{ px: 1, py: 0.5, mt: 2 }}>
                            <ListItemText
                                primary={"Personal navigator"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        fontWeight: 600,
                                        textTransform: "uppercase",
                                        letterSpacing: '0.05em'
                                    }
                                }}
                            />
                        </ListItem>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/learning"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/learning') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/learning') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/learning') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/learning') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconBook size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Personalized Learning"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/learning') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/learning') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/my-ideas"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/my-ideas') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/my-ideas') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/my-ideas') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/my-ideas') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconBulb size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Your ideas"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/my-ideas') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/my-ideas') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/conversations"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/conversations') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/conversations') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/conversations') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/conversations') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconMessage size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Conversations"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/conversations') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/conversations') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                        <ListItemButton
                            component={NavLink}
                            className="nav-link"
                            to={"/my-likes"}
                            sx={{
                                borderRadius: '8px',
                                mb: 0.5,
                                mx: 1,
                                px: 2,
                                py: 1.2,
                                bgcolor: isActivePath('/my-likes') ? theme.palette.secondary.main : 'transparent',
                                border: isActivePath('/my-likes') ? `1px solid ${theme.palette.secondary.main}` : '1px solid transparent',
                                '&:hover': {
                                    bgcolor: isActivePath('/my-likes') ? theme.palette.secondary.main : '#f8fafc'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <ListItemIcon sx={{
                                color: isActivePath('/my-likes') ? iconColor : '#64748b',
                                minWidth: '36px'
                            }}>
                                <IconHeart size={20}/>
                            </ListItemIcon>
                            <ListItemText
                                primary={"Your likes & votes"}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: isActivePath('/my-likes') ? 600 : 500,
                                        fontSize: '0.875rem',
                                        color: isActivePath('/my-likes') ? '#1e293b' : '#475569'
                                    }
                                }}
                            />
                        </ListItemButton>
                    </>}
            </List>

            <List sx={{display: {xs: "block", md: "none"}}}>
                <ListItem>
                    <ListItemText
                        primary={"Profile"}
                        primaryTypographyProps={{
                            sx: {
                                fontSize: 13,
                                color: grey[500],
                                fontWeight: 600,
                                textTransform: "uppercase"
                            }
                        }}
                    />
                </ListItem>
                {/*<ListItemButton component={NavLink} className="nav-link" to={"/notifications"}>*/}
                {/*    <ListItemIcon><IconBell/></ListItemIcon>*/}
                {/*    <ListItemText primary={"Notifications"} primaryTypographyProps={{sx: {fontColor: grey[500]}}}/>*/}
                {/*</ListItemButton>*/}
                <ListItemButton className="nav-link" onClick={async () => await signOut(auth)}>
                    <ListItemIcon><IconLogout/></ListItemIcon>
                    <ListItemText primary={"Logout"} primaryTypographyProps={{sx: {fontColor: grey[500]}}}/>
                </ListItemButton>
            </List>
        </>
    )
}
